import { forwardRef } from 'react';
import MuiGrid, { type GridProps as MuiGridProps } from '@mui/material/Grid';

/**
 * Grid component - A responsive grid layout system using CSS Grid.
 *
 * Thin wrapper around MUI v7 Grid. Responsive column widths are passed
 * via the `size` prop as a breakpoint object. Container grids use the
 * `container` prop and a `spacing` value.
 *
 * ## When to Use Grid vs Box
 *
 * **Use Grid when:**
 * - You need equal-width columns with responsive breakpoints
 * - Building form layouts with consistent field widths
 * - Creating dashboard or card gallery layouts
 * - You need complex multi-column layouts
 *
 * **Use Box with CSS Grid when:**
 * - You need explicit grid-template-areas
 * - Complex custom grid layouts
 * - See Box component for CSS Grid examples
 *
 * **Use Box with Flexbox when:**
 * - Simple horizontal/vertical layouts (most common: 70% of layouts)
 * - Dynamic spacing with gap
 * - Single-direction layouts
 *
 * @example
 * // Two-column responsive layout (most common pattern)
 * <Grid container spacing={2}>
 *   <Grid size={{ xs: 12, sm: 6 }}>
 *     <TextField label="First Name" />
 *   </Grid>
 *   <Grid size={{ xs: 12, sm: 6 }}>
 *     <TextField label="Last Name" />
 *   </Grid>
 * </Grid>
 *
 * @example
 * // Three-column card gallery
 * <Grid container spacing={3}>
 *   {items.map(item => (
 *     <Grid key={item.id} size={{ xs: 12, sm: 6, md: 4 }}>
 *       <Card>{item.content}</Card>
 *     </Grid>
 *   ))}
 * </Grid>
 *
 * @example
 * // Nested grid for complex layouts
 * <Grid container spacing={2}>
 *   <Grid size={{ xs: 12, md: 8 }}>
 *     <Grid container spacing={1}>
 *       {/* nested items *\/}
 *     </Grid>
 *   </Grid>
 *   <Grid size={{ xs: 12, md: 4 }}>
 *     {/* sidebar *\/}
 *   </Grid>
 * </Grid>
 */
export type GridProps = MuiGridProps;

/**
 * Grid component wrapper around MUI Grid.
 */
export const Grid = forwardRef<HTMLDivElement, GridProps>((props, ref) => (
  <MuiGrid ref={ref} {...props} />
));

Grid.displayName = 'Grid';
